import {
  HttpApiError,
  HttpApiMiddleware,
  HttpApiSecurity,
  OpenApi,
} from 'effect/unstable/httpapi';
import { Context } from 'effect';
import { HttpApiDecodeError, InternalServerError } from './errors';
import type { AuthSessionData } from './session';

export class CurrentSession extends Context.Service<
  CurrentSession,
  AuthSessionData
>()('CurrentSession') {}

// Auth middleware can fail with Unauthorized (bad credentials) or InternalServerError (DB/infra issues).
// `UnauthorizedNoContent`, not `Unauthorized`: v3's Unauthorized had a VOID wire
// encoding (empty 401 body, any body decodes to the error instance); v4 split
// that into `Unauthorized` (JSON `{"_tag":"Unauthorized"}` body) and the
// no-content variant. The frozen v2 contract's 401s are body-less, so the
// no-content variant is the v3-parity spelling. It still decodes to
// `HttpApiError.Unauthorized` instances (`_tag: 'Unauthorized'`), so
// `isSessionAuthenticationError` and client-side tag checks are unaffected.
// IMPORTANT: an ARRAY, not Schema.Union([...]). HttpApiBuilder derives each
// error response's HTTP status from the TOP-LEVEL ast of each schema in the
// middleware's `error` collection — a wrapping Union carries no httpApiStatus
// annotation of its own, so the whole union falls back to the 500 default and
// every middleware 401 is rendered as an empty 500 (caught 2026-07-23 by
// legacy-firebase-exchange.test.ts). As an array, each member keeps its own
// status (401 no-content / 500).
const AuthMiddlewareFailure = [
  HttpApiError.UnauthorizedNoContent,
  InternalServerError,
] as const;

/**
 * Accepts both "Bearer <token>" and raw "<token>" in the Authorization header.
 *
 * The legacy Python API sends raw Firebase ID tokens without the "Bearer " prefix.
 * During migration, we need to accept both formats so existing sessions work
 * seamlessly when the client switches to the Effect-TS API.
 *
 * Uses HttpApiSecurity.apiKey to read the raw header value (unlike
 * HttpApiSecurity.bearer which unconditionally slices off 7 characters).
 */
const legacyBearer = HttpApiSecurity.apiKey({
  key: 'authorization',
  in: 'header',
}).pipe(
  HttpApiSecurity.annotate(
    OpenApi.Description,
    'Bearer token or raw Firebase token for authentication',
  ),
);

export class UserAuthMiddleware extends HttpApiMiddleware.Service<
  UserAuthMiddleware,
  { provides: CurrentSession }
>()('UserAuthMiddleware', {
  error: AuthMiddlewareFailure,
  security: { Authorization: legacyBearer },
}) {}
export class AdminAuthMiddleware extends HttpApiMiddleware.Service<
  AdminAuthMiddleware,
  { provides: CurrentSession }
>()('AdminAuthMiddleware', {
  error: AuthMiddlewareFailure,
  security: { Authorization: legacyBearer },
}) {}

/**
 * Optional auth middleware for endpoints that accept but don't require authentication.
 *
 * Used by endpoints that serve public documents (visibility = 'public') but also
 * need to identify the user when authenticated (e.g., for progress tracking).
 * Handlers use `Effect.serviceOption(CurrentSession)` to get `Option<Session>`.
 *
 * v4 has no `optional` middleware flag; optionality is expressed by NOT
 * declaring `provides`. A v4 security middleware wraps the endpoint's response
 * effect and always runs — `securityDecode` yields an empty credential (never
 * fails) when the Authorization header is missing — so the implementation
 * decides per request: valid credentials → `Effect.provideService(httpEffect,
 * CurrentSession, session)`; missing/invalid → run `httpEffect` untouched and
 * let the handler decide whether to 401 based on the resource's visibility.
 * Declaring `provides: CurrentSession` here would instead force the
 * implementation to always supply a session (the wrapped effect's CurrentSession
 * requirement can only be eliminated by providing it), making optional auth
 * inexpressible.
 */
export class OptionalUserAuthMiddleware extends HttpApiMiddleware.Service<OptionalUserAuthMiddleware>()(
  'OptionalUserAuthMiddleware',
  {
    error: AuthMiddlewareFailure,
    security: { Authorization: legacyBearer },
  },
) {}

/**
 * Carries v3's automatically-declared request-decode 400 on every endpoint.
 *
 * Under `@effect/platform` v3, every endpoint implicitly declared (and the
 * server emitted) `HttpApiDecodeError` when the request failed schema
 * decoding. v4 removed that: decode failures raise the non-schema
 * `HttpApiSchemaError`, which renders an EMPTY 400. Declaring this
 * middleware's `error` restores the v3 contract surface on every endpoint
 * (`ApiHttpApiContract` attaches it API-wide), and v4's intended mechanism —
 * `HttpApiMiddleware.layerSchemaErrorTransform(RequestDecodeErrorMiddleware,
 * ...)` in apps/api — restores the v3-shaped wire body by converting each
 * `HttpApiSchemaError` into an `HttpApiDecodeError`. No security/provides:
 * this middleware exists to declare the error and host that transform.
 */
export class RequestDecodeErrorMiddleware extends HttpApiMiddleware.Service<RequestDecodeErrorMiddleware>()(
  'RequestDecodeErrorMiddleware',
  {
    error: HttpApiDecodeError,
  },
) {}
